import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { useApiFetch } from '../lib/api'
import { useAuth } from '../auth/useAuth'
import { LockIcon } from '../components/icons/LockIcon'
import { ThumbUpIcon } from '../components/icons/ThumbUpIcon'
import { ThumbDownIcon } from '../components/icons/ThumbDownIcon'
import { PageHeader } from '../components/PageHeader'

/* ─── Not-subscribed-state card ───
   The shared MVP "Preview" surface: a Preview badge, a short card title +
   2-3 sentence description, a tier-neutral "may require upgrade" badge, and
   the interest vote buttons. This is the ONLY interaction on a Preview
   surface — no hero, no feature grid, no product icon, no feedback form, no
   other call to action. Used by every Preview surface in Studio (Marketplace,
   Workflows, Agents Analytics, and Global Settings → Connections), so the
   composition and copy stay identical across surfaces.

   Interest votes are recorded via the shared backend endpoint (R-OBS-1) —
   each vote is tracked per tenant/user/feature/direction. */

export interface NotSubscribedCardProps {
  cardTitle: string
  description: string
  /** Identifier sent to the backend and used to disambiguate votes across surfaces. */
  voteFeature: string
  /** Overrides the default tier-agnostic req-badge copy (preview.tierBadge). Used by
   * the Agents overview, which has a confirmed subscription state rather than an
   * unreleased tier — see AgentsPreviewCard below. */
  badgeText?: string
}

export function NotSubscribedCard({
  cardTitle,
  description,
  voteFeature,
  badgeText,
}: Readonly<NotSubscribedCardProps>) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const [voted, setVoted] = useState<'up' | 'down' | null>(null)

  const voteMutation = useMutation({
    mutationFn: (direction: 'up' | 'down') =>
      apiFetch<void>('/api/v1/global-settings/preview-interest-vote', {
        method: 'POST',
        body: JSON.stringify({ feature: voteFeature, direction }),
      }),
  })

  function handleVote(direction: 'up' | 'down') {
    setVoted(direction)
    voteMutation.mutate(direction)
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="not-subscribed-state">
          <span className="ns-badge">{t('common.preview')}</span>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '4px 0', color: 'var(--text-primary)' }}>
            {cardTitle}
          </p>
          <p style={{ fontSize: 13, maxWidth: 480, margin: '0 auto' }}>
            {description}
          </p>
          <span className="req-badge">
            <LockIcon size={13} />
            {badgeText ?? t('preview.tierBadge')}
          </span>
          <div className="vote-btns">
            <button
              type="button"
              className={`vote-btn${voted === 'up' ? ' voted' : ''}`}
              onClick={() => handleVote('up')}
            >
              <ThumbUpIcon size={15} />
              {t('preview.interested')}
            </button>
            <button
              type="button"
              className={`vote-btn${voted === 'down' ? ' voted' : ''}`}
              onClick={() => handleVote('down')}
            >
              <ThumbDownIcon size={15} />
              {t('preview.notForUs')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Preview page shell ───
   The page keeps its own title + caption (the same caption shown on its Home
   quick-action card); the "Preview" badge lives on the nav item (AppShell),
   not on the page title. The card below carries its own, separate "Preview"
   badge as the first element inside it. */

export interface PreviewPageProps {
  title: string
  caption: string
  description: string
  voteFeature: string
  /** Card title, if different from the page title (see the Agents variant below,
   * whose page title is "Agents" but whose card reads "Mambu Agents"). */
  cardTitle?: string
  badgeText?: string
}

export function PreviewPage({
  title,
  caption,
  description,
  voteFeature,
  cardTitle,
  badgeText,
}: Readonly<PreviewPageProps>) {
  return (
    <div className="page-view">
      <div className="page-header">
        <PageHeader title={title} caption={caption} />
      </div>
      <div className="page-body">
        <NotSubscribedCard
          cardTitle={cardTitle ?? title}
          description={description}
          voteFeature={voteFeature}
          badgeText={badgeText}
        />
      </div>
    </div>
  )
}

/** Card copy shared by Marketplace/Workflows/Analytics when the tenant is not
 * contracted for Mambu Agents (MS-741 AC2.3) — the caller already sees these
 * three surfaces are Preview-tier, and their *only* unreleased dependency is
 * Agents, so the card swaps to the Agents card's title/description/badge
 * while the page keeps its own title/caption. Mirrors the prototype's
 * rewrite of #agentsnc's title/caption per section (agent-studio.html:7283-7286). */
function useAgentsPreviewCardOverride(): { cardTitle: string; description: string; badgeText: string } | null {
  const { agentsContracted } = useAuth()
  const { t } = useTranslation()
  if (agentsContracted) return null
  return {
    cardTitle: t('previewPage.agents.cardTitle'),
    description: t('previewPage.agents.description'),
    badgeText: t('preview.agentsContactBadge'),
  }
}

export function Marketplace() {
  const { t } = useTranslation()
  const override = useAgentsPreviewCardOverride()
  return (
    <PreviewPage
      title={t('appShell.nav.marketplace')}
      caption={t('home.cards.marketplace.desc')}
      description={override?.description ?? t('previewPage.marketplace.description')}
      voteFeature="Marketplace"
      cardTitle={override?.cardTitle}
      badgeText={override?.badgeText}
    />
  )
}

export function Workflows() {
  const { t } = useTranslation()
  const override = useAgentsPreviewCardOverride()
  return (
    <PreviewPage
      title={t('appShell.nav.workflows')}
      caption={t('home.cards.workflows.desc')}
      description={override?.description ?? t('previewPage.workflows.description')}
      voteFeature="Workflows"
      cardTitle={override?.cardTitle}
      badgeText={override?.badgeText}
    />
  )
}

export function Analytics() {
  const { t } = useTranslation()
  const override = useAgentsPreviewCardOverride()
  return (
    <PreviewPage
      title={t('appShell.nav.analytics')}
      caption={t('home.cards.analytics.desc')}
      description={override?.description ?? t('previewPage.analytics.description')}
      voteFeature="Agents Analytics"
      cardTitle={override?.cardTitle}
      badgeText={override?.badgeText}
    />
  )
}

/* ─── Agents overview preview card (MS-737 AC7) ───
   Rendered by the Agents route instead of the real Agents overview when the
   tenant has no active Mambu Agents subscription (profile.agentsContracted is
   false). Unlike the other Preview surfaces above, this has a confirmed
   subscription state rather than an unreleased tier, so its req-badge copy
   directs the caller to contact Mambu instead of the tier-agnostic
   preview.tierBadge text — see prototype #section-agentsnc. */

export function AgentsPreview() {
  const { t } = useTranslation()
  return (
    <PreviewPage
      title={t('agents.heading')}
      caption={t('agents.subtitle')}
      cardTitle={t('previewPage.agents.cardTitle')}
      description={t('previewPage.agents.description')}
      voteFeature="Mambu Agents"
      badgeText={t('preview.agentsContactBadge')}
    />
  )
}
